import { ApiError, TransportError } from "@tdelivr/api-client";

/**
 * The states every screen here has to render, and the four the design does not draw.
 *
 * `10 Rider App.dc.html` draws eleven screens and not one of them is a loading, forbidden, conflict
 * or offline state. That is not an omission to work around silently: the rules say build every
 * designed state, and these four are states this application reaches and the design is silent about.
 * So each is rendered in the design's **own vocabulary** — the sheet, the state chip, the title, the
 * sub-line and at most one 52dp action — rather than in a look nobody drew, and the words are this
 * build's and flagged as such.
 *
 * Two of the six matter more here than they would on a console, and for reasons specific to a phone
 * at a kerbside in Nairobi:
 *
 *   - **offline** — a rider loses signal in a basement car park while a driver is on the way. The
 *     rider design has no banner for it, unlike the driver design's `6 Offline queue`, so it is the
 *     sheet with the design's own `unknown` chip. What it must never be is the happy path with stale
 *     numbers on it: a position drawn as current when nothing could be fetched is R-23 again.
 *   - **unconfirmable** — EXP-002's whole shape. `stage` has ten values and `unknown` is the tenth;
 *     `finalFare`, `vehicle`, `dispatch`, `driver`, `position` and `eta` each publish a typed reason
 *     for their absence; `position.confirmed` is a judgement with its own evidence beside it. The
 *     design supplies the pill for all of it — dashed, mono, carrying a `?` — and `4 Confirming`
 *     draws it full size as `not confirmed yet`. A state is never shown by colour alone and is never
 *     shown as its nearest confident neighbour.
 */

export type ViewState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready" }
  /** The caller's token lacks a scope. Names it, because a rider told the name can ask for it. */
  | { readonly kind: "forbidden"; readonly missingScopes: readonly string[] }
  /** The request never reached a service. */
  | { readonly kind: "offline" }
  | {
      readonly kind: "error";
      readonly code: string;
      readonly message: string;
      /** Whether offering a retry is honest. A button that cannot work teaches button-mashing. */
      readonly retryable: boolean;
      /** True for a 409, which `3 Quote` reaches by confirming a ride twice. */
      readonly conflict: boolean;
    };

/**
 * Turn whatever went wrong into the state a screen can render.
 *
 * Exhaustive over the platform's eight error categories rather than a default case, so a category
 * added to the runtime fails the build here instead of being rendered as a generic failure.
 */
export function stateForError(error: unknown, missingScopes: readonly string[] = []): ViewState {
  if (error instanceof TransportError) return { kind: "offline" };

  if (!(error instanceof ApiError)) {
    return {
      kind: "error",
      code: "unexpected_error",
      message: error instanceof Error ? error.message : "Something went wrong",
      retryable: false,
      conflict: false,
    };
  }

  switch (error.category) {
    case "authorization":
    case "authentication":
      return { kind: "forbidden", missingScopes };
    case "not_found":
      /*
       * A 404 is a fact about the subject rather than an outage: there is no such quote, or no such
       * ride request in this tenant. Drawn as an error naming it, because a blank screen would read
       * as "nothing is happening" to somebody waiting for a car.
       */
      return {
        kind: "error",
        code: error.code,
        message: error.message,
        retryable: false,
        conflict: false,
      };
    case "conflict":
      /*
       * The designed 409. `CLAUDE.md` section 1 names conflict among the states that are designed and
       * must be built, and on this application it has exactly one source: an `Idempotency-Key`
       * reused with materially different terms, which Document 06 says is refused rather than
       * answered from the first request. That is the one thing a rider pressing `Confirm ride` twice
       * must not be told quietly, because the two outcomes are "you have a ride" and "you have a
       * different ride".
       */
      return {
        kind: "error",
        code: error.code,
        message: error.message,
        retryable: false,
        conflict: true,
      };
    case "validation":
    case "rate_limit":
    case "dependency":
    case "internal":
      return {
        kind: "error",
        code: error.code,
        message: error.message,
        retryable: error.retryable,
        conflict: false,
      };
  }
}

/** What to put on a retry control, if anything. */
export function retryLabel(state: ViewState): string | undefined {
  if (state.kind === "offline") return "Try again";
  if (state.kind === "error" && state.retryable) return "Try again";
  return undefined;
}
